import React, {
  type ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { KibDateField } from '@chewy/kib-calendar-react'
import {
  KibCheckbox,
  type KibCheckboxModelValue,
} from '@chewy/kib-choices-react'
import { KibField } from '@chewy/kib-fields-new-react'
import { moment } from '@pbt/pbt-ui-components'

import type { DataHandle, Prescription } from '~/types'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import '@chewy/kib-calendar-styles/dist/kib-calendar.css'
import style from './SwapProductRefill.module.scss'

export interface SwapProductRefillProps {
  onRefChange?: () => void
  prescription: Prescription
}

export interface SwapProductRefillHandle extends DataHandle {}

const SwapProductRefill = forwardRef<
  SwapProductRefillHandle,
  SwapProductRefillProps
>(function SwapProductRefill({ onRefChange, prescription }, ref) {
  const { t } = useTranslation(['Common', 'Dialogs', 'Tooltips', 'Tasks'])

  const [isRefillable, setIsRefillable] = useState<KibCheckboxModelValue>(
    Boolean(prescription.quantityNumbRefills),
  )
  const [refillableQuantity, setRefillableQuantity] = useState<
    number | undefined
  >(prescription.quantityNumbRefills || 1)
  const [refillExpirationDate, setRefillExpirationDate] = useState<string>(
    prescription.quantityRefillExpirationDate
      ? moment
          .utc(prescription.quantityRefillExpirationDate)
          .format('MM/DD/YYYY')
      : '',
  )

  useImperativeHandle(
    ref,
    () => ({
      get: () => {
        // Convert date from MM/DD/YYYY to ISO format for backend (without time)
        const expirationDateISO =
          isRefillable && refillExpirationDate
            ? moment
                .utc(refillExpirationDate, 'MM/DD/YYYY')
                .startOf('day')
                .toISOString()
            : null

        return {
          quantityNumbRefills: isRefillable
            ? Number(refillableQuantity) || 0
            : 0,
          quantityRefillExpirationDate: expirationDateISO,
        }
      },
      // Parent form handles HTML5 validation
      validate: () => Boolean(true),
    }),
    [isRefillable, refillExpirationDate, refillableQuantity],
  )

  useEffectExceptOnMount(() => {
    setIsRefillable(Boolean(prescription.quantityNumbRefills))
    setRefillableQuantity(prescription.quantityNumbRefills || 1)
    setRefillExpirationDate(
      prescription.quantityRefillExpirationDate
        ? moment
            .utc(prescription.quantityRefillExpirationDate)
            .format('MM/DD/YYYY')
        : '',
    )
  }, [
    prescription.quantityNumbRefills,
    prescription.quantityRefillExpirationDate,
  ])

  useEffect(() => {
    onRefChange?.()
  }, [isRefillable, refillableQuantity, refillExpirationDate, onRefChange])
  return (
    <div className={style.refillable}>
      <KibCheckbox
        label={t('Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLABLE')}
        selectedValue={isRefillable}
        onChange={(val) => setIsRefillable(val)}
      />
      {isRefillable && (
        <>
          <KibField
            required
            label={t(
              'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
            )}
            min="1"
            type="number"
            value={refillableQuantity}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              setRefillableQuantity(
                value === '' ? undefined : parseInt(value, 10),
              )
            }}
          />
          <KibDateField
            required
            showCalendar
            className={style.dateFieldPlaceholder}
            format="MM/dd/yyyy"
            help=""
            label={t(
              'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILL_EXPIRATION',
            )}
            {...(prescription.createdAt &&
              moment.utc(prescription.createdAt).isValid() && {
                max: moment
                  .utc(prescription.createdAt)
                  // Within 1 year: 364 days to match backend validation
                  .add(364, 'days')
                  .format('YYYY-MM-DD'),
              })}
            placeholder="mm/dd/yyyy"
            value={refillExpirationDate}
            onChange={(e) => setRefillExpirationDate(e.target.value)}
          />
        </>
      )}
    </div>
  )
})

export default SwapProductRefill
